// deps
import React, { memo, useMemo } from "react";
import cx from "classnames";

// components
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import DeliveryTagInfo from "./DeliveryTagInfo";

// types
import { DeliveryTagInfoUIData } from "@super-ui/interfaces/SwiggyOne";

// helpers
import { getBoldTitle, getPrefixedTestId } from "@super-ui/helpers/String";
import { AriaHidden } from "@swiggy-private/core-ui";

// constants
import { ONE_WIDGET_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { SWIGGY_ONE_ICONS_HW, TITLE_BOLD } from "@super-ui/constants/SwiggyOne";

import {
    Container,
    ContainerWithBorder,
    HeaderContainer,
    HeaderIconImageContainer,
    HeaderText,
    DeliveryTagInfoContainer,
} from "../styles/HeaderWithDeliveryInfo.styles";
import styles from "../styles/SwiggyOneWidget.v4.scss";

export interface HeaderWithDeliveryInfoProps {
    title: string;
    showTitleShimmer: boolean;
    logoCtx: {
        logo: string;
    };
    testIdPrefix?: string;
    isOneLitePlan?: boolean;
    deliveryTagUIInfo?: DeliveryTagInfoUIData;
    isBenefitListPresent?: boolean;
}

const HeaderWithDeliveryInfo: React.FC<HeaderWithDeliveryInfoProps> = ({
    title = "",
    showTitleShimmer,
    logoCtx: { logo = "" },
    testIdPrefix,
    isOneLitePlan = false,
    deliveryTagUIInfo,
    isBenefitListPresent = false,
}: HeaderWithDeliveryInfoProps) => {
    const headerTestId = useMemo(() => getPrefixedTestId(ONE_WIDGET_TEST_IDS.HEADER, testIdPrefix), [testIdPrefix]);
    const headerLogoTestId = useMemo(() => getPrefixedTestId(ONE_WIDGET_TEST_IDS.HEADER_LOGO, testIdPrefix), [
        testIdPrefix,
    ]);
    const boldTitle = useMemo(() => getBoldTitle(title, TITLE_BOLD.OPEN, TITLE_BOLD.CLOSE), [title]);
    const iconWidth = useMemo(
        () => (isOneLitePlan ? SWIGGY_ONE_ICONS_HW.ONE_LITE.width : SWIGGY_ONE_ICONS_HW.ONE.width),
        [isOneLitePlan],
    );
    const iconHeight = useMemo(
        () => (isOneLitePlan ? SWIGGY_ONE_ICONS_HW.ONE_LITE.height : SWIGGY_ONE_ICONS_HW.ONE.height),
        [isOneLitePlan],
    );
    const custombackground = useMemo(() => ({ light: "", dark: "" }), []);
    // we have shimmer capability already built in scss so using it directly instead of replicating for styled-component
    const headerContainerShimmer = cx({ [styles.headerContainerShimmer]: showTitleShimmer });
    return (
        <Container>
            <ContainerWithBorder isBenefitListPresent={isBenefitListPresent}>
                <AriaHidden as="div">
                    <HeaderContainer className={headerContainerShimmer} data-testid={headerTestId}>
                        <HeaderIconImageContainer width={iconWidth} height={iconHeight} data-testid={headerLogoTestId}>
                            <Image
                                cloudinaryId={logo}
                                lazyLoad={false}
                                width={iconWidth}
                                height={iconHeight}
                                customBackground={custombackground}
                            />
                        </HeaderIconImageContainer>
                        <HeaderText
                            variant="V2.body1.neutral.bold"
                            dangerouslySetInnerHTML={{ __html: boldTitle }}
                            isOneLitePlan={isOneLitePlan}
                        />
                    </HeaderContainer>
                </AriaHidden>
            </ContainerWithBorder>
            {deliveryTagUIInfo && (
                <DeliveryTagInfoContainer>
                    <DeliveryTagInfo data={deliveryTagUIInfo} />
                </DeliveryTagInfoContainer>
            )}
        </Container>
    );
};

export default memo(HeaderWithDeliveryInfo);
